import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Mail, RefreshCw, UserPlus } from 'lucide-react';
import type { Employee } from '../../../store/slices/employeeSlice';
import type { AuthRole } from '../../../utils/roles';
import { Sheet } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { generateTempPassword } from './AccessDialogs';
import { ROLE_OPTIONS, ROLE_SUMMARY, roleLabel } from './roles';

export interface CreateUserForm {
  fullName: string;
  email: string;
  role: AuthRole;
  employeeId: string;
  mode: 'invite' | 'password';
  temporaryPassword: string;
}

export const EMPTY_CREATE_FORM: CreateUserForm = {
  fullName: '',
  email: '',
  role: 'employee',
  employeeId: '',
  mode: 'invite',
  temporaryPassword: '',
};

const FORM_ID = 'create-user-form';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Name plus email, so two people with the same name stay distinguishable once one is picked. */
const employeeLabel = (employee: Employee) => (employee.email ? `${employee.name} · ${employee.email}` : employee.name);

/**
 * Add user (book §11.14 sheet): name, email, role, an optional employee link,
 * and how they first sign in. The parent owns the request; this owns the form.
 */
const CreateUserSheet: React.FC<{
  open: boolean;
  form: CreateUserForm;
  onChange: (next: CreateUserForm) => void;
  employees: Employee[];
  pending: boolean;
  error: string;
  onClose: () => void;
  onSubmit: () => void;
}> = ({ open, form, onChange, employees, pending, error, onClose, onSubmit }) => {
  const [submitted, setSubmitted] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) setSubmitted(false);
  }, [open]);

  const set = <K extends keyof CreateUserForm>(key: K, value: CreateUserForm[K]) => onChange({ ...form, [key]: value });
  const email = form.email.trim();
  const emailProblem = !email
    ? 'Enter a work email to create the login.'
    : !EMAIL_PATTERN.test(email)
      ? 'Enter the full work email, like maria.chen@sbgfunding.com.'
      : null;
  const passwordProblem = form.mode === 'password' && !form.temporaryPassword ? 'Enter a password or generate one.' : null;
  const emailError = submitted ? emailProblem : null;
  const passwordMissing = submitted && Boolean(passwordProblem);
  const linkedEmployee = employees.find((employee) => employee._id === form.employeeId) ?? null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (pending) return;
    // Move focus to the first field that needs attention (book §11.4).
    if (emailProblem) {
      emailRef.current?.focus();
      return;
    }
    if (passwordProblem) {
      passwordRef.current?.focus();
      return;
    }
    onSubmit();
  };

  const busyLabel = form.mode === 'invite' ? 'Sending invite…' : 'Creating user…';
  const submitLabel = form.mode === 'invite' ? 'Send invite' : 'Create user';

  return (
    <Sheet
      open={open}
      onClose={pending ? () => undefined : onClose}
      title="Add user"
      subtitle="Create a login, choose its role, and decide how they first sign in."
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            disabled={pending}
            aria-busy={pending || undefined}
            startIcon={pending ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : form.mode === 'invite' ? <Mail /> : <UserPlus />}
          >
            {pending ? busyLabel : submitLabel}
          </Button>
        </>
      }
    >
      <Box component="form" id={FORM_ID} noValidate onSubmit={submit} sx={{ display: 'grid', gap: 2.5 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Box component="section" aria-labelledby="create-user-person" sx={{ display: 'grid', gap: 2 }}>
          <Typography id="create-user-person" component="h3" variant="h4" sx={{ color: sbg.text.primary }}>
            Person
          </Typography>
          <TextField
            label="Full name (optional)"
            placeholder="e.g. Maria Chen"
            value={form.fullName}
            onChange={(event) => set('fullName', event.target.value)}
            disabled={pending}
            fullWidth
            slotProps={{ htmlInput: { autoComplete: 'off' } }}
          />
          <TextField
            label="Work email"
            type="email"
            placeholder="e.g. maria.chen@sbgfunding.com"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
            disabled={pending}
            error={Boolean(emailError)}
            helperText={emailError ?? undefined}
            fullWidth
            inputRef={emailRef}
            slotProps={{ htmlInput: { autoComplete: 'off', spellCheck: false } }}
          />
          <Autocomplete
            options={employees}
            value={linkedEmployee}
            onChange={(_, employee) => set('employeeId', employee?._id ?? '')}
            getOptionLabel={employeeLabel}
            getOptionKey={(employee) => employee._id}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            disabled={pending}
            renderOption={({ key, ...props }, employee) => (
              <li key={key} {...props}>
                <Box sx={{ display: 'grid', minWidth: 0 }}>
                  <Typography variant="body1" component="span" sx={{ color: sbg.text.primary }}>
                    {employee.name}
                  </Typography>
                  <Typography variant="caption" component="span" noWrap sx={{ color: sbg.text.secondary }}>
                    {employee.email}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Linked employee (optional)"
                placeholder="Search employees"
                helperText="Connects this login to their employee record."
              />
            )}
          />
        </Box>

        <Box
          component="section"
          aria-labelledby="create-user-access"
          sx={{ display: 'grid', gap: 2, pt: 2.5, borderTop: `1px solid ${sbg.border.subtle}` }}
        >
          <Typography id="create-user-access" component="h3" variant="h4" sx={{ color: sbg.text.primary }}>
            Access
          </Typography>
          <FormControl fullWidth disabled={pending}>
            <InputLabel id="create-user-role-label">Role</InputLabel>
            <Select
              labelId="create-user-role-label"
              label="Role"
              value={form.role}
              onChange={(event) => set('role', event.target.value as AuthRole)}
              renderValue={(value) => roleLabel(value as AuthRole)}
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role}>
                  <Box sx={{ display: 'grid' }}>
                    <Typography variant="body1" component="span">{roleLabel(role)}</Typography>
                    <Typography variant="caption" component="span" sx={{ color: sbg.text.secondary }}>
                      {ROLE_SUMMARY[role]}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{ROLE_SUMMARY[form.role]}</FormHelperText>
          </FormControl>

          <FormControl disabled={pending}>
            <FormLabel id="create-user-mode-label" sx={{ mb: 0.75 }}>
              First sign-in
            </FormLabel>
            <RadioGroup
              aria-labelledby="create-user-mode-label"
              value={form.mode}
              onChange={(event) => set('mode', event.target.value as CreateUserForm['mode'])}
            >
              <FormControlLabel value="invite" control={<Radio />} label="Send invite email" />
              <FormControlLabel value="password" control={<Radio />} label="Set a temporary password" />
            </RadioGroup>
          </FormControl>

          {form.mode === 'password' && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <TextField
                label="Temporary password"
                value={form.temporaryPassword}
                onChange={(event) => set('temporaryPassword', event.target.value)}
                disabled={pending}
                error={passwordMissing}
                helperText={passwordMissing ? passwordProblem : 'Share it with them directly.'}
                fullWidth
                inputRef={passwordRef}
                slotProps={{ htmlInput: { autoComplete: 'off', spellCheck: false } }}
              />
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshCw />}
                  disabled={pending}
                  onClick={() => set('temporaryPassword', generateTempPassword())}
                >
                  Generate password
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Sheet>
  );
};

export default CreateUserSheet;
